<template>
  <div
    class="modal fade"
    id="modal"
    ref="modal"
    data-bs-backdrop="static"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px;">
        <div class="modal-header border-0 pb-2">
          <h5 class="modal-title fw-bold text-dark">
            <i class="fa-solid fa-plus-circle me-2 text-theme"/>
            {{ $t('New Session') }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"/>
        </div>

        <div class="modal-body px-4 pb-4">
            <div class="mb-3">
              <label class="form-label small fw-bold text-muted mb-2">
                {{ $t('Order Type') }}
              </label>
              <div class="row g-2">
                <div class="col-4" v-for="t in types" :key="t.value">
                  <button
                    class="btn w-100 py-2 fw-bold rounded-3"
                    :class="[form.type === t.value ? 'btn-theme text-white' : 'btn-outline-secondary']"
                    @click="selectType(t.value)"
                  >
                    <i :class="t.icon" class="mb-1"/>
                    <span class="small d-block">{{ $t(t.label) }}</span>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="form.type === 'dine_in'" class="mb-3">
              <label class="form-label small fw-bold text-muted">
                <i class="fa-solid fa-chair me-1"/>{{ $t('Table') }}
                <span class="text-muted fw-normal">({{ $t('optional') }})</span>
                <i v-if="loadingTables" class="fa-solid fa-spinner fa-spin ms-1"/>
              </label>
              <select
                class="form-select shadow-none"
                v-model="form.table_id"
                :disabled="loadingTables"
              >
                <option :value="null">
                  {{ loadingTables ? $t('Loading') + '...' : $t('No table') }}
                </option>
                <option
                  v-for="tbl in tables"
                  :key="tbl.id"
                  :value="tbl.id"
                >
                  {{ tbl.name }}<span v-if="tbl.zone"> — {{ $t(tbl.zone) }}</span>
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label small fw-bold text-muted">
                <i class="fa-solid fa-id-card me-1"/>{{ $t('Member') }}
                <span class="text-muted fw-normal">({{ $t('optional') }})</span>
              </label>
              <select2
                @select="onSelectCustomer"
                @change="onChangeCustomer"
                :options="select2Customer.options"
                :settings="select2Customer.setting"
                :placeholder="$t('Search member by phone or name') + '...'"
              />
            </div>

            <div class="mb-3">
              <label class="form-label small fw-bold text-muted">
                <i class="fa-solid fa-user me-1"/>{{ $t('Customer Name') }}
              </label>
              <input
                type="text"
                class="form-control shadow-none"
                v-model.trim="form.customer_name"
                :placeholder="$t('Walk-in')"
              >
            </div>

            <div class="row g-2 mb-3">
              <div :class="form.type === 'dine_in' ? 'col-12' : 'col-7'">
                <label class="form-label small fw-bold text-muted">
                  <i class="fa-solid fa-phone me-1"/>{{ $t('Phone') }}
                </label>
                <input
                  type="tel"
                  class="form-control shadow-none"
                  v-model.trim="form.customer_phone"
                  placeholder="08X-XXX-XXXX"
                >
              </div>
              <div v-if="form.type !== 'dine_in'" class="col-5">
                <label class="form-label small fw-bold text-muted">
                  <i class="fa-regular fa-clock me-1"/>{{ $t('Pickup') }}
                </label>
                <input
                  type="time"
                  class="form-control shadow-none"
                  v-model="form.pickup_time"
                >
              </div>
            </div>

            <div>
              <label class="form-label small fw-bold text-muted">
                <i class="fa-solid fa-comment-dots me-1"/>{{ $t('Note') }}
              </label>
              <input
                type="text"
                class="form-control shadow-none"
                v-model.trim="form.note"
                :placeholder="$t('e.g. No spicy, extra sauce')+'...'"
              >
            </div>
        </div>

        <div class="modal-footer border-0 pt-0 px-4 pb-4">
          <button type="button" class="btn btn-outline-secondary rounded-3 px-4" data-bs-dismiss="modal">
            {{ $t('Cancel') }}
          </button>
          <button
            class="btn btn-theme text-white fw-bold rounded-3 px-4 flex-grow-1"
            :disabled="!form.type || loadingSave"
            @click="handleSave"
          >
            <i class="fa-solid fa-spinner fa-spin me-2" v-if="loadingSave"/>
            <i class="fa-solid fa-check me-2" v-else/>
            {{ $t('Create Session') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Sessions, POSTables } from '@/services/endpoint/pos.service';
import { AUTH } from '@/services/endpoint/_constants';
import Select2 from 'vue3-select2-component';
import signature from '@/services/signature';
import moment from 'moment';

export default {
  name: 'ModalNewSession',
  emits: ['close', 'saved'],
  components: { Select2 },
  data() {
    const vm = this;
    return {
      loadingSave: false,
      loadingTables: true,
      tables: [],
      types: [
        { value: 'dine_in',  label: 'Dine-in',  icon: 'fa-solid fa-utensils' },
        { value: 'takeaway', label: 'Takeaway', icon: 'fa-solid fa-bag-shopping' },
        { value: 'delivery', label: 'Delivery', icon: 'fa-solid fa-motorcycle' }
      ],
      form: {
        type: 'dine_in',
        table_id: null,
        customer_id: null,
        customer_name: '',
        customer_phone: '',
        pickup_time: '',
        note: ''
      },
      select2Customer: {
        options: [],
        setting: {
          dropdownParent: '#modal',
          allowClear: true,
          minimumInputLength: 1,
          language: {
            loadingMore: () => '<i class="fa-solid fa-spinner fa-spin" />',
            searching: () => vm.$t('Searching') + '...',
            inputTooShort: () => vm.$t('Type to search') + '...',
            noResults: () => vm.$t('No members found')
          },
          ajax: {
            beforeSend: (request) => request.setRequestHeader('Authorization', 'Bearer ' + AUTH.TOKEN),
            url: AUTH.URL + '/v1/customers',
            data: function (params) {
              var query = { search: params.term, page: params.page || 1, limit: 10 };
              return signature($.param(query)).substring(1);
            },
            processResults: function (data) {
              var option = (data.data || []).map(function (c) {
                return {
                  id: c.id,
                  text: (c.fullname || '-') + (c.telephone1 ? ' · ' + c.telephone1 : '') + (c.member_code ? ' · ' + c.member_code : ''),
                  fullname: c.fullname,
                  telephone: c.telephone1
                };
              });
              return {
                results: option,
                pagination: { more: (data.meta.current_page * 10) < data.meta.total }
              };
            }
          }
        }
      }
    };
  },
  methods: {
    async fetchTables() {
      this.loadingTables = true;
      await POSTables.list('').then(res => {
        this.tables = (res.data.data || []).filter(t => t.state === 'Available');
      }).catch(() => {
        this.tables = [];
      }).finally(() => {
        this.loadingTables = false;
      });
    },
    onSelectCustomer(e) {
      this.form.customer_id = e.id;
      if (e.fullname) this.form.customer_name = e.fullname;
      if (e.telephone) this.form.customer_phone = e.telephone;
    },
    onChangeCustomer(val) {
      if (!val) this.form.customer_id = null;
    },
    selectType(value) {
      this.form.type = value;
      if (value !== 'dine_in') {
        this.form.table_id = null;
      }
      if (value === 'dine_in') {
        this.form.pickup_time = '';
      }
    },
    buildPayload() {
      const payload = {
        type: this.form.type,
        table_id: null,
        customer_id: this.form.customer_id || null,
        customer_name: this.form.customer_name || '',
        telephone: this.form.customer_phone || '',
        pickup_at: null,
        note: this.form.note || ''
      };
      if (this.form.type === 'dine_in') {
        payload.table_id = this.form.table_id || null;
      } else if (this.form.pickup_time) {
        // Backend expects strict "YYYY-MM-DD HH:mm:ss"; <input type="time">
        // gives "HH:mm" so prefix today's date in local time.
        payload.pickup_at = moment().format('YYYY-MM-DD') + ' ' + this.form.pickup_time + ':00';
      }
      return payload;
    },
    async handleSave() {
      if (this.loadingSave) return;
      this.loadingSave = true;
      const payload = this.buildPayload();
      await Sessions.open(payload).then(res => {
        const token = res?.data?.data?.token || '';
        const msg = res?.data?.data?.message || this.$t('Session opened');
        this.$toast.success('<b>' + this.$t('Success') + '</b> ' + msg);
        $(this.$refs.modal).modal('hide');
        this.$emit('saved', { token });
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(err.response.data.message);
        }
      }).finally(() => {
        this.loadingSave = false;
      });
    }
  },
  mounted() {
    $(this.$refs.modal).modal('show');
    $(this.$refs.modal).on('hidden.bs.modal', () => this.$emit('close'));
    this.fetchTables();
  }
};
</script>
